import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Play, X, Radar, ScanText, ClipboardList, ListTree, MessageSquare, Users,
  UserPlus, FolderPlus, TrendingUp, Star, ChevronRight, CalendarCheck,
} from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import TrustBar from "@/components/TrustBar";
import {
  TwoModules, SectorStrip, PlatformNumbers, EpcToOm, FieldFeatures, FieldVoices, PricingCta,
} from "@/components/marketing/HomeSections";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/lib/useMeta";
import { INDUSTRY_LIST } from "@/pages/industry/industryData";

const GOLD = "#C9A84C";
const HERO = "https://images.unsplash.com/photo-1784911545613-391bb6853d00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxvaWwlMjByZWZpbmVyeSUyMGluZHVzdHJpYWwlMjBwbGFudCUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3ODc5OTg3NjN8MA&ixlib=rb-4.1.0&q=85&w=1920";

const STATS = [
  [500, "+", "Activities tracked per project package"],
  [2000, " Cr+", "Contract portfolios the platform is built to handle"],
  [98, "%", "DPR compliance achievable with daily validation"],
  [7, "", "EPC sectors covered across India"],
];

const FEATURES = [
  [Radar, "Risk Radar", "AI-powered delay forecasting that bands every work front red, amber or green weeks before a milestone slips."],
  [ScanText, "BOQ Parser", "Import Excel, CSV or PDF BOQs; quantities, units and rates become a live WBS after your planner confirms them."],
  [ClipboardList, "DPR Management", "Daily progress with manpower, idle hours, weather and photo evidence — logged from a phone, even with no signal."],
  [ListTree, "WBS Tracker", "Activity-level control in real units — Cu.M, MT, Dia-Inch — with balance quantity and float computed for you."],
  [MessageSquare, "Site Alerts", "Automated progress and risk updates pushed to the people who need to act, not buried in a mailbox."],
  [Users, "Multi-Role Access", "Six roles from site engineer to super admin, enforced in the database with a full audit trail on every edit."],
];

const STEPS = [
  [UserPlus, "Sign up and verify", "Create your company account with a work email and verify it. The 14-day trial starts immediately — no card required."],
  [FolderPlus, "Set up your project", "Create the project package, import your WBS and BOQ, set baseline dates and add your team with the right roles."],
  [TrendingUp, "Track, forecast, deliver", "Site engineers log daily progress; the S-curve, critical path, earned value and AI risk alerts update themselves."],
];

const TESTIMONIALS = [
  ["Plan-Sarthi cut our DPR reporting cycle by more than half. What used to be three engineers and a weekend is now one person and twenty minutes.", "Rajesh Kumar", "Project Manager", "ABC Infrastructure Ltd."],
  ["The critical path finally reflects site reality, so our extension-of-time notices go out with evidence attached instead of arguments.", "S. Venkatesan", "Planning Head", "Southern EPC Projects"],
  ["We spotted a slipping structural front six weeks before it would have hit the boiler erection window. That single catch paid for the year.", "Amit Deshpande", "Construction Manager", "Thermal Power Division"],
];

function useCountUp(target, active) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1400, 1);
      setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, active]);
  return value;
}

const Stat = ({ target, suffix, label, active, prefix }) => {
  const value = useCountUp(target, active);
  return (
    <div data-testid={`stat-${target}`}>
      <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold num" style={{ color: GOLD }}>
        {prefix}{value.toLocaleString("en-IN")}{suffix}
      </div>
      <p className="text-[12px] sm:text-[12.5px] text-white/70 mt-2 leading-relaxed">{label}</p>
    </div>
  );
};

const TOUR = [
  ["command-center.png", "Command Center", "Progress, S-curve, CPI/SPI and cash-out on one screen."],
  ["dpr.png", "Daily Progress Report", "The field log that recomputes every downstream number."],
  ["risk-radar.png", "Risk Radar", "Forecast finish dates and value at risk per work front."],
];

const DemoModal = ({ onClose }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % TOUR.length), 4500);
    return () => clearInterval(t);
  }, []);
  const [file, title, caption] = TOUR[i];
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="demo-tour-modal">
      <div className="w-full max-w-[980px] bg-[#0A1628] border border-white/15 rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <span className="text-[13px] font-medium text-white" data-testid="tour-title">{title}</span>
          <span className="text-[11.5px] text-white/50 hidden sm:inline">{caption}</span>
          <button onClick={onClose} className="ml-auto text-white/60 hover:text-white" data-testid="tour-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <img src={`/shots/${file}`} alt={title} className="w-full block" data-testid="tour-image" />
        <div className="flex items-center gap-2 px-4 py-3 border-t border-white/10">
          {TOUR.map(([f], idx) => (
            <button
              key={f}
              onClick={() => setI(idx)}
              className="h-1.5 rounded-full transition-all duration-200"
              style={{ width: idx === i ? 28 : 12, background: idx === i ? GOLD : "rgba(255,255,255,0.25)" }}
              data-testid={`tour-dot-${idx}`}
            />
          ))}
          <Link to="/demo" className="ml-auto inline-flex items-center gap-1.5 text-[12.5px] font-medium" style={{ color: GOLD }} data-testid="tour-book-demo-link">
            <CalendarCheck className="w-3.5 h-3.5" /> Book a live 30-minute demo
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const { user } = useAuth();
  const [tour, setTour] = useState(false);
  const [statsIn, setStatsIn] = useState(false);
  const statsRef = useRef(null);

  useMeta({
    title: "Plan-Sarthi — AI-Powered EPC Project Management and O&M Software India",
    description:
      "Plan Smarter. Build Stronger. Deliver on Time. India's first AI-powered EPC project management and O&M platform for site engineers, project managers and contractors across solar, wind, thermal, nuclear, HVAC and civil projects. 14-day free trial, no card required.",
    path: "/",
  });

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setStatsIn(true), { threshold: 0.35 });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  if (user) return <Navigate to="/projects" replace />;

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="home-page">
      <PublicHeader />

      <section className="relative overflow-hidden" data-testid="hero-section">
        <img src={HERO} alt="Industrial EPC construction site" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/92 to-[#0A1628]/70" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-block border px-2.5 py-1 text-[12px] font-semibold uppercase"
                  style={{ borderColor: "rgba(201,168,76,0.5)", color: GOLD, letterSpacing: "0.12em" }}
                  data-testid="hero-label-pill">
              INDIA'S FIRST AI-POWERED EPC AND O&amp;M PLATFORM
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-5" data-testid="hero-headline">
              Plan Smarter. Build Stronger.{" "}
              <span style={{ color: GOLD }}>Deliver on Time.</span>
            </h1>
            <p className="text-white mt-6 max-w-[560px]" style={{ fontSize: 18, lineHeight: 1.6 }} data-testid="hero-subheadline">
              India's first AI-powered EPC project management and O&amp;M platform — built for site engineers,
              project managers, and contractors across Solar, Wind, Thermal, Nuclear, Engineering EPC, HVAC,
              Civil, and other infrastructure projects.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/signup" className="inline-flex items-center h-12 px-7 rounded-lg text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
                    style={{ backgroundColor: GOLD }} data-testid="hero-trial-btn">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <button onClick={() => setTour(true)}
                      className="inline-flex items-center h-12 px-7 rounded-lg border border-white/50 text-[15px] font-medium text-white hover:bg-white/10 transition-colors duration-200"
                      data-testid="hero-demo-btn">
                <Play className="w-4 h-4 mr-2" /> Watch Demo
              </button>
            </div>
            <Link to="/om/sectors" className="inline-flex items-center gap-1.5 text-[14px] font-semibold mt-5 hover:underline"
                  style={{ color: GOLD }} data-testid="hero-om-link">
              Explore O&amp;M Module <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <p className="text-[12px] text-white/45 mt-5">14 days free · No credit card required · Cancel anytime</p>
          </div>

          <div className="hidden lg:block border border-white/15 rounded-lg overflow-hidden shadow-2xl shadow-black/60" data-testid="hero-mockup">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#112240] border-b border-white/10">
              <span className="w-2 h-2 rounded-full bg-red-500/70" />
              <span className="w-2 h-2 rounded-full bg-amber-500/70" />
              <span className="w-2 h-2 rounded-full bg-emerald-500/70" />
              <span className="num text-[10px] text-white/40 ml-2">plansarthi.com — Command Center</span>
            </div>
            <img src="/shots/command-center.png" alt="Plan-Sarthi Command Center dashboard" className="w-full block" />
          </div>
        </div>
      </section>

      <section ref={statsRef} className="bg-[#112240] border-y border-white/10" data-testid="stats-bar">        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map(([target, suffix, label], i) => (
            <Stat key={label} target={target} suffix={suffix} label={label} active={statsIn} prefix={i === 1 ? "₹" : ""} />
          ))}
        </div>
      </section>

      <TwoModules />
      <SectorStrip />
      <PlatformNumbers />
      <EpcToOm />
      <FieldFeatures />
      <FieldVoices />
      <PricingCta />

      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="features-section">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-center">
          Everything You Need to <span style={{ color: GOLD }}>Deliver on Time</span>
        </h2>
        <p className="text-[14px] text-white/60 text-center mt-4 max-w-[640px] mx-auto leading-relaxed">
          One workspace replaces the stack of spreadsheets your planning team maintains at 2 AM before a client review.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {FEATURES.map(([Icon, title, text], i) => (
            <div
              key={title}
              className="group bg-[#112240] border border-white/10 border-t-2 border-t-transparent rounded-lg p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/50"
              style={{ borderTopColor: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderTopColor = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderTopColor = "transparent"; }}
              data-testid={`feature-card-${i}`}
            >
              <Icon className="w-6 h-6" style={{ color: GOLD }} />
              <h3 className="font-semibold text-[15.5px] mt-4">{title}</h3>
              <p className="text-[13px] text-white/60 mt-2.5 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/features" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium" style={{ color: GOLD }} data-testid="features-section-link">
            See every module on real screens <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="bg-[#112240] border-y border-white/10" data-testid="how-it-works-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-center">
            Up and Running in <span style={{ color: GOLD }}>Minutes</span>
          </h2>
          <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-8 md:gap-5 items-start mt-12">
            {STEPS.map(([Icon, title, text], i) => (
              <div key={title} className="contents">
                <div data-testid={`step-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-3xl font-bold num" style={{ color: GOLD }}>0{i + 1}</span>
                    <Icon className="w-5 h-5" style={{ color: GOLD }} />
                  </div>
                  <h3 className="font-semibold text-[15.5px] mt-4">{title}</h3>
                  <p className="text-[13px] text-white/60 mt-2.5 leading-relaxed">{text}</p>
                </div>
                {i < STEPS.length - 1 ? (
                  <div className="hidden md:flex items-center justify-center pt-3">
                    <ArrowRight className="w-6 h-6" style={{ color: "rgba(201,168,76,0.5)" }} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/signup" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold text-white"
                  style={{ backgroundColor: GOLD }} data-testid="how-it-works-trial-btn">
              Create your workspace <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="testimonials-section">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-center">
          Trusted by <span style={{ color: GOLD }}>EPC Professionals</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {TESTIMONIALS.map(([quote, name, role, company], i) => (
            <div key={name} className="bg-[#112240] border border-white/10 rounded-lg p-6 flex flex-col" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1">
                {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5" style={{ color: GOLD, fill: GOLD }} />)}
              </div>
              <p className="text-[13.5px] italic text-white/80 mt-4 leading-relaxed flex-1">"{quote}"</p>
              <div className="mt-5 pt-4 border-t border-white/10 text-[12px]">
                <div className="font-medium text-white">{name}</div>
                <div className="text-white/50 mt-0.5">{role} · {company}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/35 text-center mt-6">
          Illustrative testimonials from early access engagements. Named references available on request.
        </p>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 pb-16" data-testid="home-industries">
        <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em] text-center" style={{ color: GOLD }}>
          Built for your sector
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-7">
          {INDUSTRY_LIST.map((ind) => (
            <Link
              key={ind.slug}
              to={`/industry/${ind.slug}`}
              className="flex flex-col items-center gap-2 bg-[#112240] border border-white/10 rounded-lg p-4 text-center hover:border-[#C9A84C]/50 transition-colors duration-200"
              data-testid={`home-industry-${ind.slug}`}
            >
              <ind.icon className="w-5 h-5" style={{ color: GOLD }} />
              <span className="text-[12px] leading-tight">{ind.name}</span>
            </Link>
          ))}
        </div>
      </section>

      <section style={{ backgroundColor: GOLD }} data-testid="final-cta-banner">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A1628]">
              Ready to transform your project delivery?
            </h2>
            <p className="text-[13.5px] text-[#0A1628]/75 mt-2">
              Start free, bring one live work front, and see the S-curve build itself from your daily reports.
            </p>
          </div>
          <Link
            to="/signup"
            className="md:ml-auto shrink-0 inline-flex items-center h-12 px-7 rounded-lg bg-[#0A1628] text-white text-[15px] font-semibold hover:bg-[#112240] transition-colors duration-200"
            data-testid="final-cta-btn"
          >
            Start Your Free 14-Day Trial <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>

      <TrustBar />
      <Footer />
      <BackToTop />
      {tour ? <DemoModal onClose={() => setTour(false)} /> : null}
    </div>
  );
}
